import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Coins } from "lucide-react";
import { Logo } from "@/components/Logo";
import { saas } from "@/lib/saasApi";
import { getPlan } from "@/lib/plans";
import { SupportLine } from "@/components/SupportLine";
import { useI18n, usePageTitle } from "@/i18n";

// Informational + polling. This page NEVER grants credits and NEVER trusts the session_id
// query param as proof of payment. The verified Stripe webhook (invoice.paid) is the source
// of truth; here we simply poll the customer's own billing state until it turns active.
const MAX_POLLS = 20;      // ~50s
const INTERVAL_MS = 2500;

export default function CheckoutSuccess() {
  const { t } = useI18n();
  usePageTitle("titles.checkoutSuccess");
  const [billing, setBilling] = useState(null);
  const [phase, setPhase] = useState("confirming"); // confirming | ready | slow
  const timer = useRef(null);
  const tries = useRef(0);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      tries.current += 1;
      try {
        const { data } = await saas.billing();
        if (!active) return;
        setBilling(data);
        if (data.subscriptionStatus === "active" && data.availableCredits > 0) {
          setPhase("ready");
          clearInterval(timer.current);
          return;
        }
      } catch {
        /* not signed in yet or transient — keep the confirming message */
      }
      if (tries.current >= MAX_POLLS) {
        setPhase((p) => (p === "ready" ? p : "slow"));
        clearInterval(timer.current);
      }
    };
    poll();
    timer.current = setInterval(poll, INTERVAL_MS);
    return () => { active = false; clearInterval(timer.current); };
  }, []);

  const planName = billing?.billing?.planName || getPlan(billing?.planId)?.name || null;
  const credits = billing?.availableCredits;

  return (
    <div className="flex min-h-screen items-center justify-center bg-re-soft px-6 py-16" data-testid="checkout-success-page">
      <div className="w-full max-w-md rounded-2xl border border-re-line bg-white p-8 text-center shadow-sm">
        <Link to="/dashboard" className="mb-6 inline-flex justify-center"><Logo /></Link>

        {phase === "ready" ? (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
            <h1 className="mt-4 font-heading text-2xl font-bold text-re-charcoal" data-testid="checkout-success-title">{t("app.checkout.readyTitle")}</h1>
            <div className="mt-4 space-y-2 rounded-xl bg-re-soft p-4 text-left">
              {planName && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-re-muted">{t("app.checkout.activatedPlan")}</span>
                  <span className="font-semibold text-re-charcoal" data-testid="success-plan-name">{planName}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-re-muted">{t("app.checkout.availableCredits")}</span>
                <span className="inline-flex items-center gap-1.5 font-semibold text-re-charcoal" data-testid="success-credits">
                  <Coins className="h-4 w-4 text-re-red" /> {credits}
                </span>
              </div>
            </div>
            <div className="mt-7 flex flex-col gap-3">
              <Link to="/projects/new" data-testid="success-create-project"
                className="rounded-full bg-re-red px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-re-deepred">
                {t("app.checkout.createFirst")}
              </Link>
              <Link to="/dashboard" data-testid="success-dashboard"
                className="rounded-full border border-re-line px-6 py-3 text-sm font-semibold text-re-ink transition-colors hover:border-re-charcoal">
                {t("app.checkout.toDashboard")}
              </Link>
            </div>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-re-red" />
            <h1 className="mt-4 font-heading text-2xl font-bold text-re-charcoal" data-testid="checkout-success-title">
              {phase === "slow" ? t("app.checkout.almostThere") : t("app.checkout.confirming")}
            </h1>
            <p className="mx-auto mt-3 max-w-sm text-sm text-re-muted" data-testid="success-status-text">
              {phase === "slow"
                ? t("app.checkout.slowText")
                : t("app.checkout.confirmingText")}
            </p>
            <div className="mt-7">
              <Link to="/dashboard" data-testid="success-dashboard"
                className="inline-block rounded-full border border-re-line px-6 py-3 text-sm font-semibold text-re-ink transition-colors hover:border-re-charcoal">
                {t("app.checkout.toDashboard")}
              </Link>
            </div>
          </>
        )}
        <div className="mt-6 border-t border-re-line pt-4">
          <SupportLine className="justify-center" testid="checkout-success-support" />
        </div>
      </div>
    </div>
  );
}
